import React from 'react';
import {
  selectAssetComponentTypes,
  selectAvailLicense,
  selectAvailPlatform,
  selectAvailScheduleStatuses,
  selectAvailTerritory,
  selectEntityStatuses,
  selectEntityTypeLabels,
  selectExceptionReferenceTypes,
  selectExceptionSeverities,
  selectExceptionSubtypes,
  selectExceptionTypes,
  selectHierarchies,
  selectJobPriorityLevels,
  selectJobTypes,
  selectLanguagesFilter,
  selectNetworkCodes,
  selectOrderCategories,
  selectProfileCustomers,
  selectShortListLanguagesFilter,
  selectStages,
  selectTaskQCTypes,
  selectTaskTypeGroups,
  selectVfsTaskTypes
} from '@/redux/selectors/constantsSelectors';
import { UserProfile } from '@/redux/slices/slice_types';
import FilterUser from './FilterUser';
import GroupFilter from '../AssigneeSelect/GroupFilter';
import {
  selectEntityTypesFilter,
  selectBillableCustomersFilter,
  selectNonBillableCustomersFilter,
  selectCustomerFilter,
  selectFileTypeFilter,
  selectStatusFilter,
  selectContentTypes
} from '@/redux/selectors/dataTableFiltersSelectors';
import { FilterIDType } from './FilterID';
import { RootState } from '@/redux/store';
import FilterProfiles from '@/components/_common/Filters/FilterProfiles';
import { FilterBehavior } from './Filter';

export enum NewFilterTypesMap {
  ARRAY = 'array',
  ID = 'ID',
  STRING = 'string',
  DATE = 'date',
  BOOLEAN = 'boolean',
  USERS = 'users',
  SEARCH = 'search',
  TEXT = 'text',
  NULLABLE_BOOLEAN = 'nullable_boolean',
  NUMERIC_RANGE = 'numeric_range',
  PROFILES = 'profiles'
}

type FilterBase = {
  id: string; // key to be passed to the BE in api call body; if typeof DATE, id is used to store values in querystring
  label: string;
  field?: string;
  options?: (RootState: RootState) => Record<string, string> | Record<number, string>;
  excludeOptions?: (RootState: RootState) => Record<string, string> | Record<number, string>;
  filterBehavior?: FilterBehavior;
};

export type FilterArray = FilterBase & {
  type: NewFilterTypesMap.ARRAY;
  searchable?: boolean;
  singleSelection?: boolean;
};

export type FilterUsers = FilterBase & {
  type: NewFilterTypesMap.USERS;
  component?: React.FC;
  renderApplied?: (...args: any) => string;
};

export type FilterProfiles = FilterBase & {
  type: NewFilterTypesMap.PROFILES;
  component?: React.FC;
};

export type FilterOther = FilterBase & {
  type: Exclude<NewFilterTypesMap, NewFilterTypesMap.ARRAY | NewFilterTypesMap.USERS | NewFilterTypesMap.PROFILES>;
  limit?: number;
};

export type FilterType = FilterArray | FilterOther | FilterUsers | FilterProfiles;

// NOTE: any dispatch calls that need to be made prior to the options being generated
//   should be done prior to the TableContainer mounting

const defaultFilters: Record<string, FilterType> = {
  status: {
    id: 'status_filter',
    label: 'Status',
    type: NewFilterTypesMap.ARRAY,
    options: selectStatusFilter
  },
  orderHid: {
    id: 'order_hid_filter',
    label: 'Order ID',
    type: NewFilterTypesMap.TEXT
  },
  jobHid: {
    id: 'job_hid_filter',
    label: 'Job ID',
    type: NewFilterTypesMap.TEXT
  },
  taskHid: {
    id: 'task_hid_filter',
    label: 'Task ID',
    type: NewFilterTypesMap.TEXT
  },
  ingestHid: {
    id: 'ingest_hid_filter',
    label: 'Ingest ID',
    type: NewFilterTypesMap.TEXT
  },
  filterId: {
    id: 'filter_id',
    label: 'Search by ID',
    excludeOptions: () => ({
      [FilterIDType.JOB_BUNDLE_ID]: FilterIDType.JOB_BUNDLE_ID
    }),
    type: NewFilterTypesMap.ID
  },
  filterIdWithBundle: {
    id: 'filter_id',
    label: 'Search by ID',
    type: NewFilterTypesMap.ID
  },
  filterIdNoJobAsset: {
    id: 'filter_id',
    label: 'Search by ID',
    excludeOptions: () => ({
      [FilterIDType.ASSET_ID]: FilterIDType.ASSET_ID
    }),
    type: NewFilterTypesMap.ID
  },
  filterIdNoTask: {
    id: 'filter_id',
    label: 'Search by ID',
    excludeOptions: () => ({
      [FilterIDType.TASK_ID]: FilterIDType.TASK_ID,
      [FilterIDType.PROCESSING_TASKS]: FilterIDType.PROCESSING_TASKS
    }),
    type: NewFilterTypesMap.ID
  },
  jobAssetHid: {
    id: 'job_asset_hid_filter',
    label: 'Asset ID',
    type: NewFilterTypesMap.TEXT
  },
  customer: {
    id: 'customer_filter',
    label: 'Customer',
    searchable: true,
    options: selectCustomerFilter,
    type: NewFilterTypesMap.ARRAY
  },
  customerID: {
    id: 'customer_id_filter',
    label: 'Customer',
    searchable: true,
    options: selectCustomerFilter,
    type: NewFilterTypesMap.ARRAY
  },
  customerWithProfiles: {
    id: 'customer_filter',
    label: 'Customer',
    searchable: true,
    options: selectProfileCustomers,
    type: NewFilterTypesMap.ARRAY
  },
  profile: {
    id: 'profile_filter',
    label: 'Profile',
    component: (props) => <FilterProfiles {...props} />,
    type: NewFilterTypesMap.PROFILES
  },
  managedBy: {
    id: 'managed_by_filter',
    label: 'Managed By',
    component: (props) => <FilterUser type="managed_by_filter" {...props} />,
    renderApplied: (user: UserProfile) => `${user.first_name} ${user.last_name}`,
    type: NewFilterTypesMap.USERS
  },
  search: {
    id: 'search',
    label: 'Search',
    type: NewFilterTypesMap.SEARCH
  },
  jobType: {
    id: 'job_type_filter',
    label: 'Type',
    type: NewFilterTypesMap.ARRAY,
    options: selectJobTypes,
    searchable: true
  },
  stage: {
    id: 'stage_filter',
    label: 'Stage',
    type: NewFilterTypesMap.ARRAY,
    options: selectStages,
    searchable: true
  },
  dueDate: {
    id: 'date',
    label: 'Due Date',
    type: NewFilterTypesMap.DATE
  },
  earliestJobDueDate: {
    id: 'earliest_job_due_date',
    label: 'Earliest Job Due Date',
    field: 'earliest_job_due_date',
    type: NewFilterTypesMap.DATE
  },
  completedAt: {
    id: 'completed_at',
    label: 'Completed Date',
    field: 'completed_at',
    type: NewFilterTypesMap.DATE
  },
  createdAt: {
    id: 'created_at',
    label: 'Created Date',
    field: 'created_at',
    type: NewFilterTypesMap.DATE
  },
  qcByDate: {
    id: 'qc_by_date',
    label: 'QC By',
    field: 'qc_by_date',
    type: NewFilterTypesMap.DATE
  },
  linearTxDate: {
    id: 'linear_tx_date',
    label: 'TX Date',
    field: 'linear_tx_date',
    type: NewFilterTypesMap.DATE
  },
  componentType: {
    id: 'component_type_filter',
    label: 'Type',
    type: NewFilterTypesMap.ARRAY,
    options: selectAssetComponentTypes
  },
  assignedGroups: {
    id: 'assigned_groups_filter',
    label: 'Assigned Group',
    component: (props) => <GroupFilter assigns="group" isTableFilter {...props} />,
    renderApplied: (data) => data.group,
    type: NewFilterTypesMap.USERS
  },
  assignedUsers: {
    id: 'assigned_users_filter',
    label: 'Assignee',
    component: (props) => <FilterUser {...props} />,
    renderApplied: (user) => `${user.first_name} ${user.last_name}`,
    type: NewFilterTypesMap.USERS
  },
  entityType: {
    id: 'entity_types_filter', // override with 'ui_entity_types_filter' to supercede regular filter
    label: 'Type',
    options: selectEntityTypesFilter,
    type: NewFilterTypesMap.ARRAY
  },
  entity_status: {
    id: 'status_filter',
    label: 'Status',
    type: NewFilterTypesMap.ARRAY,
    options: selectEntityStatuses
  },
  hierarchy: {
    id: 'hierarchy_filter',
    label: 'Business Unit',
    options: selectHierarchies,
    type: NewFilterTypesMap.ARRAY,
    singleSelection: true
  },
  taskType: {
    id: 'name_filter',
    label: 'Type',
    options: selectTaskTypeGroups,
    type: NewFilterTypesMap.ARRAY
  },
  showNotReady: {
    id: 'show_not_ready',
    label: 'Show Future Tasks',
    type: NewFilterTypesMap.BOOLEAN
  },
  showResolved: {
    id: 'show_resolved',
    label: 'Include Resolved',
    type: NewFilterTypesMap.BOOLEAN
  },
  fileType: {
    id: 'file_type_filter',
    label: 'Type',
    searchable: true,
    type: NewFilterTypesMap.ARRAY,
    options: selectFileTypeFilter
  },
  billableClient: {
    id: 'billable_customer_filter',
    label: 'Client Name',
    type: NewFilterTypesMap.ARRAY,
    searchable: true,
    options: selectBillableCustomersFilter
  },
  nonBillableCustomer: {
    id: 'customer_filter',
    label: 'Customer',
    type: NewFilterTypesMap.ARRAY,
    searchable: true,
    options: selectNonBillableCustomersFilter
  },
  contentType: {
    id: 'type_filter',
    label: 'Content Type',
    type: NewFilterTypesMap.ARRAY,
    searchable: true,
    options: selectContentTypes
  },
  titleHidFilter: {
    id: 'title_hid_filter',
    label: 'Search',
    type: NewFilterTypesMap.SEARCH
  },
  versionHidFilter: {
    id: 'version_hid_filter',
    label: 'Search',
    type: NewFilterTypesMap.SEARCH
  },
  orderCategory: {
    id: 'category_filter',
    label: 'Category',
    searchable: true,
    type: NewFilterTypesMap.ARRAY,
    options: selectOrderCategories
  },
  outputNetwork: {
    id: 'output_network_name_filter',
    label: 'Network',
    type: NewFilterTypesMap.ARRAY,
    options: selectNetworkCodes
  },
  broadcastLength: {
    id: 'broadcast_length',
    field: 'broadcast_length',
    label: 'Broadcast Length',
    limit: 3,
    type: NewFilterTypesMap.NUMERIC_RANGE
  },
  transferQueueAction: {
    id: 'task_type_filter',
    field: 'task_type_filter',
    label: 'Action',
    type: NewFilterTypesMap.ARRAY,
    options: selectVfsTaskTypes
  },
  language: {
    id: 'language_filter',
    label: 'Language',
    type: NewFilterTypesMap.ARRAY,
    options: selectLanguagesFilter,
    searchable: true
  },
  exceptionsType: {
    id: 'type_filter',
    label: 'Type',
    options: selectExceptionTypes,
    type: NewFilterTypesMap.ARRAY
  },
  exceptionsSubtype: {
    id: 'subtype_filter',
    label: 'Subtype',
    options: selectExceptionSubtypes,
    type: NewFilterTypesMap.ARRAY
  },
  exceptionsReferenceType: {
    id: 'reference_type_filter',
    label: 'Related To',
    options: selectExceptionReferenceTypes,
    type: NewFilterTypesMap.ARRAY
  },
  exceptionsSeverity: {
    id: 'severity_filter',
    label: 'Severity',
    options: selectExceptionSeverities,
    type: NewFilterTypesMap.ARRAY
  },
  filterIdException: {
    id: 'filter_id',
    label: 'Search by ID',
    type: NewFilterTypesMap.ID
  },
  resolvedAt: {
    id: 'resolved_at',
    label: 'Resolved Date',
    field: 'resolved_at',
    type: NewFilterTypesMap.DATE
  },
  entityTypeLabels: {
    id: 'entity_types_filter',
    label: 'Type',
    options: selectEntityTypeLabels,
    type: NewFilterTypesMap.ARRAY
  },
  job_priority_level: {
    id: 'job_priority_level_filter',
    label: 'Title Priority',
    type: NewFilterTypesMap.ARRAY,
    options: selectJobPriorityLevels,
    searchable: true
  },
  qc_task_types: {
    id: 'qc_task_types_filter',
    label: 'QC Task Types',
    type: NewFilterTypesMap.ARRAY,
    options: selectTaskQCTypes,
    searchable: true
  },
  avails_territory: {
    id: 'territory_filter',
    label: 'Territory',
    options: selectAvailTerritory,
    type: NewFilterTypesMap.ARRAY
  },
  avails_timed_text: {
    id: 'subs_filter',
    label: 'Subs',
    options: selectShortListLanguagesFilter,
    type: NewFilterTypesMap.ARRAY
  },
  avails_audio: {
    id: 'dubs_filter',
    label: 'Dubs',
    options: selectShortListLanguagesFilter,
    type: NewFilterTypesMap.ARRAY
  },
  avails_license: {
    id: 'license_filter',
    label: 'License',
    options: selectAvailLicense,
    type: NewFilterTypesMap.ARRAY
  },
  avails_platform: {
    id: 'availed_platform_filter',
    label: 'Platform',
    options: selectAvailPlatform,
    type: NewFilterTypesMap.ARRAY
  },
  avails_schedule_status: {
    id: 'status_filter',
    label: 'Status',
    options: selectAvailScheduleStatuses,
    type: NewFilterTypesMap.ARRAY
  },
  avails_language: {
    id: 'availed_av_language_filter',
    label: 'AV Language',
    //options: selectAvailLanguage,
    options: selectShortListLanguagesFilter,
    type: NewFilterTypesMap.ARRAY
  },
  term_start: {
    id: 'term_start',
    label: 'Term Start',
    field: 'term_start',
    type: NewFilterTypesMap.DATE
  },
  term_end: {
    id: 'term_end',
    label: 'Term End',
    field: 'term_end',
    type: NewFilterTypesMap.DATE
  },
  title_alt_client_id_filter: {
    id: 'title_alt_client_id_filter',
    label: 'Alpha IDs',
    type: NewFilterTypesMap.TEXT
  }
};

export default defaultFilters;
